// Tabelle für Kontakte und Aktivitäten (Runde 5, Agent 5C), im Aufbau wie `LeadTabelle`: Spaltenköpfe mit Sortierung, Tastatur
// (j/k und Pfeile, Enter öffnet, x markiert), Fokuszeile, Markierungskästchen (Umschalt-Klick markiert einen Bereich),
// Nachladen am Ende. Zeilenhöhe, Linien und Schrift kommen aus `.tabelle` (app.css), die Breiten aus den Spaltenklassen.
import { useEffect, useRef, type ReactNode } from "react";
import { Icon } from "../../components/Icon";
import { useTasten } from "../../werkzeuge/tasten";
import { useSpaltenZiehen } from "../../components/SpaltenZiehen";

export interface TabellenSpalte {
  key: string;
  label: string;
  sortierbar?: boolean;
  klasse?: string;
  /** Tooltip und Vorlesetext für Spalten ohne sichtbare Überschrift (z. B. die Symbolspalte Telefon/E-Mail) */
  kopfTitel?: string;
  /** bleibt an seinem Platz (nicht verschiebbar), z. B. die Symbolspalte Telefon/E-Mail */
  fest?: boolean;
}

export const AUSWAHL_SPALTE = "__auswahl";

export interface TabellenAuswahl {
  markiert: ReadonlySet<string>;
  onAendern: (neu: Set<string>) => void;
  /** Vorlesetext des Kästchens einer Zeile, z. B. „Max Muster markieren“ */
  beschriftung: (zeileId: string) => string;
  alleTitel: string;
}

interface Props<T> {
  spalten: TabellenSpalte[];
  zeilen: T[];
  zeilenId: (z: T) => string;
  zelle: (z: T, s: TabellenSpalte) => ReactNode;
  fokus: number;
  setFokus: (i: number) => void;
  onOeffnen: (z: T, i: number) => void;
  sort?: { key: string; ab: boolean } | null;
  onSort?: (key: string) => void;
  /** wird aufgerufen, wenn das Tabellenende sichtbar wird */
  onEnde?: () => void;
  fuss?: ReactNode;
  klasse?: string;
  auswahl?: TabellenAuswahl;
  zeilenKlasse?: (z: T) => string | undefined;
  /** false: Kürzel (j, k, Enter, x) ausschalten, z. B. solange ein Dialog offen ist */
  tastenAktiv?: boolean;
  /** Spaltenbreiten in px je Spaltenschlüssel (je Nutzer gespeichert) */
  breiten?: Record<string, number>;
  /** Breite gezogen (px) oder per Doppelklick zurückgesetzt (null) */
  onBreite?: (key: string, px: number | null) => void;
  /** Spalte per Ziehen im Kopf verschoben: `von` steht danach vor `nach` (die erste Datenspalte und die Markierung bleiben) */
  onVerschieben?: (von: string, nach: string) => void;
}

export function ListenTabelle<T>({
  spalten,
  zeilen,
  zeilenId,
  zelle,
  fokus,
  setFokus,
  onOeffnen,
  sort,
  onSort,
  onEnde,
  fuss,
  klasse,
  auswahl,
  zeilenKlasse,
  tastenAktiv = true,
  breiten,
  onBreite,
  onVerschieben,
}: Props<T>) {
  const koerper = useRef<HTMLTableSectionElement>(null);
  const ende = useRef<HTMLDivElement>(null);
  const endeRef = useRef(onEnde);
  endeRef.current = onEnde;
  const letzterKlick = useRef<number | null>(null);
  // erste Datenspalte (Name) und Markierungsspalte bleiben an ihrem Platz
  const nameKey = spalten.find((s) => s.key !== AUSWAHL_SPALTE)?.key;
  const ziehen = useSpaltenZiehen({ breiten, onBreite, onVerschieben, fest: (k) => k === AUSWAHL_SPALTE || k === nameKey || !!spalten.find((s) => s.key === k)?.fest });

  const alleAn = !!auswahl && zeilen.length > 0 && zeilen.every((z) => auswahl.markiert.has(zeilenId(z)));
  const einigeAn = !!auswahl && !alleAn && zeilen.some((z) => auswahl.markiert.has(zeilenId(z)));

  const alleUmschalten = () => {
    if (!auswahl) return;
    const neu = new Set(auswahl.markiert);
    for (const z of zeilen) (alleAn ? neu.delete(zeilenId(z)) : neu.add(zeilenId(z)));
    auswahl.onAendern(neu);
  };

  const zeileUmschalten = (i: number, bereich: boolean) => {
    if (!auswahl) return;
    const neu = new Set(auswahl.markiert);
    const an = !neu.has(zeilenId(zeilen[i]));
    const hatLetzten = bereich && letzterKlick.current !== null && letzterKlick.current < zeilen.length;
    const von = hatLetzten ? Math.min(letzterKlick.current as number, i) : i;
    const bis = hatLetzten ? Math.max(letzterKlick.current as number, i) : i;
    for (let k = von; k <= bis; k++) (an ? neu.add(zeilenId(zeilen[k])) : neu.delete(zeilenId(zeilen[k])));
    letzterKlick.current = i;
    auswahl.onAendern(neu);
  };

  useEffect(() => {
    koerper.current?.querySelector<HTMLElement>(`tr[data-index="${fokus}"]`)?.scrollIntoView({ block: "nearest" });
  }, [fokus]);

  useEffect(() => {
    const el = ende.current;
    if (!el) return;
    const beobachter = new IntersectionObserver(
      (e) => {
        if (e.some((x) => x.isIntersecting)) endeRef.current?.();
      },
      { rootMargin: "300px" },
    );
    beobachter.observe(el);
    return () => beobachter.disconnect();
  }, []);

  useTasten(
    {
      j: () => setFokus(Math.min(fokus + 1, zeilen.length - 1)),
      ArrowDown: () => setFokus(Math.min(fokus + 1, zeilen.length - 1)),
      k: () => setFokus(Math.max(fokus - 1, 0)),
      ArrowUp: () => setFokus(Math.max(fokus - 1, 0)),
      Enter: () => {
        const z = zeilen[fokus];
        if (z) onOeffnen(z, fokus);
      },
      x: () => {
        if (zeilen[fokus]) zeileUmschalten(fokus, false);
      },
    },
    tastenAktiv,
  );

  return (
    <div className="tabelle-rahmen">
      <table className={`tabelle tabelle--leads tabelle--liste${klasse ? ` ${klasse}` : ""}`}>
        <thead>
          <tr>
            {spalten.map((s) => {
              if (auswahl && s.key === AUSWAHL_SPALTE) {
                return (
                  <th key={s.key} className={s.klasse}>
                    <input
                      type="checkbox"
                      checked={alleAn}
                      ref={(el) => {
                        if (el) el.indeterminate = einigeAn;
                      }}
                      title={auswahl.alleTitel}
                      aria-label={auswahl.alleTitel}
                      disabled={!zeilen.length}
                      onChange={alleUmschalten}
                    />
                  </th>
                );
              }
              const aktiv = sort?.key === s.key;
              return (
                <th key={s.key} {...ziehen.kopfEigenschaften(s.key, s.klasse ?? "")} aria-sort={aktiv ? (sort?.ab ? "descending" : "ascending") : undefined}>
                  {s.sortierbar && onSort ? (
                    <button type="button" className={`tabelle__sort${aktiv ? " tabelle__sort--an" : ""}`} onClick={() => onSort(s.key)} title={`${s.label}: sortieren`}>
                      <span className="tabelle__sort-text">{s.label}</span>
                      {aktiv ? <Icon name={sort?.ab ? "pfeil-runter" : "pfeil-hoch"} groesse={13} /> : <Icon name="sortieren" groesse={13} className="tabelle__sort-icon" />}
                    </button>
                  ) : (
                    <span className="tabelle__kopf" title={s.kopfTitel ?? s.label}>
                      {s.label || <span className="nur-vorlesen">{s.kopfTitel}</span>}
                    </span>
                  )}
                  {ziehen.griff(s.key, s.label || s.kopfTitel || "")}
                </th>
              );
            })}
            <th className="tabelle__rest" aria-hidden="true" />
          </tr>
        </thead>
        <tbody ref={koerper}>
          {zeilen.map((z, i) => {
            const id = zeilenId(z);
            const klassen = [i === fokus ? "tabelle__zeile--fokus" : "", auswahl?.markiert.has(id) ? "tabelle__zeile--markiert" : "", zeilenKlasse?.(z) ?? ""].filter(Boolean).join(" ");
            return (
              <tr
                key={id}
                data-index={i}
                className={klassen || undefined}
                aria-selected={i === fokus}
                onClick={(e) => {
                  if ((e.target as HTMLElement).closest("button, a, input")) return;
                  setFokus(i);
                  onOeffnen(z, i);
                }}
              >
                {spalten.map((s) =>
                  auswahl && s.key === AUSWAHL_SPALTE ? (
                    <td key={s.key} className={s.klasse}>
                      <input
                        type="checkbox"
                        checked={auswahl.markiert.has(id)}
                        aria-label={auswahl.beschriftung(id)}
                        onChange={() => undefined}
                        onClick={(e) => {
                          e.stopPropagation();
                          zeileUmschalten(i, e.shiftKey);
                        }}
                      />
                    </td>
                  ) : (
                    <td key={s.key} className={s.klasse} style={ziehen.zellStil(s.key)}>
                      <div className="zelle" style={ziehen.inhaltStil(s.key)}>{zelle(z, s)}</div>
                    </td>
                  ),
                )}
                <td className="tabelle__rest" aria-hidden="true" />
              </tr>
            );
          })}
        </tbody>
      </table>
      <div ref={ende} className="tabelle__ende" />
      {fuss}
    </div>
  );
}
